/*
 * Sticky Add To Cart — V1 ("Proposal 4")
 *
 * Trigger is intentionally identical to component-lp-pdp-sticky-cta.js, the bar the
 * other PDP sections use:
 * an IntersectionObserver on the in-page ATC/Personalize button reveals the bar once
 * that button has scrolled above the top of the viewport. The only differences are
 * that this variant also runs on desktop and that the bar is server-rendered markup
 * (see snippets/lp-pdp-sticky-atc-v1.liquid) rather than a cloned button.
 *
 * The real button stays the single source of truth: clicks are forwarded to it, and
 * its label / disabled / back-in-stock state is mirrored onto the proxy.
 */
(function () {
  var REDO_BASE_BOTTOM = 20; // the widget's own resting offset

  /*
   * The Redo chat bubble renders inside an open shadow root with `bottom: 20px` set
   * as an inline style, so neither theme CSS nor an inline write survives its
   * re-renders. Inject one !important rule bound to a custom property instead:
   * custom properties inherit through the shadow boundary, so afterwards the bubble
   * is driven purely by setting that property on the host element.
   *
   * Returns a setter, or null if the widget isn't on the page yet (it loads async,
   * so resolution is retried on each toggle until it lands).
   */
  function resolveRedoLift() {
    var host = document.getElementById('redo-chat-widget');
    var root = host && host.shadowRoot;
    if (!root) return null;

    if (!root.querySelector('[data-lp-sticky-atc-lift]')) {
      var style = document.createElement('style');
      style.setAttribute('data-lp-sticky-atc-lift', '');
      /* Two selectors: the bubble's current position in Redo's markup, plus a
         structure-agnostic match on the inline `position: fixed` it always carries,
         so a vendor markup change degrades to "bubble doesn't move" rather than an
         error. */
      style.textContent =
        '#redo-chat-widget-container > div,' +
        '#redo-chat-widget-container [style*="position: fixed"]{' +
        'bottom:var(--lp-sticky-atc-lift,' + REDO_BASE_BOTTOM + 'px)!important;' +
        'transition:bottom .25s ease}';
      root.appendChild(style);
    }

    return function (offset) {
      host.style.setProperty('--lp-sticky-atc-lift', (offset || REDO_BASE_BOTTOM) + 'px');
    };
  }

  /* The reveal depends on IntersectionObserver and the live label/price state on
   * MutationObserver. Both are baseline in every browser Shopify supports, but if
   * either is absent, remove the bar and stop rather than throwing part-way through
   * init and leaving a permanently invisible bar in the DOM. The page's own Add to
   * cart button is untouched, so the product stays fully purchasable.
   */
  function observersSupported() {
    return (
      typeof window.IntersectionObserver === 'function' &&
      typeof window.MutationObserver === 'function'
    );
  }

  function initStickyAtcV1() {
    var bars = document.querySelectorAll('[data-js-sticky-atc-v1]');
    if (!bars.length) return;

    if (!observersSupported()) {
      Array.prototype.forEach.call(bars, function (bar) {
        bar.remove();
      });
      return;
    }

    Array.prototype.forEach.call(bars, function (bar) {
      if (bar.dataset.lpStickyAtcV1Init === 'true') return;

      var section = bar.closest('[data-section-id]');
      if (!section) return;

      // The in-page ATC / Personalize button rendered by the section's product form.
      var realBtn = section.querySelector('[data-js-sticky-cta] button');
      if (!realBtn) {
        // No form on this product — a sticky ATC would have nothing to trigger.
        bar.remove();
        return;
      }

      bar.dataset.lpStickyAtcV1Init = 'true';

      var proxyBtn = bar.querySelector('[data-js-sticky-atc-v1-btn]');
      var proxyLabel = bar.querySelector('[data-js-sticky-atc-v1-label]');
      var proxyPrice = bar.querySelector('[data-js-sticky-atc-v1-price]');
      var proxyPriceLine = bar.querySelector('[data-js-sticky-atc-v1-price-line]');
      var pagePrice = section.querySelector('.lp-pdp__price');
      /* While the sale runs, the page shows the sale price from <sale-pricing>
         (component-sale-pricing.liquid) in place of .lp-pdp__price, so the bar mirrors that. */
      var salePrice = section.querySelector('[data-sale-pricing-main] [data-sale-pricing-final]');

      if (proxyBtn) {
        proxyBtn.addEventListener('click', function (event) {
          event.preventDefault();
          if (realBtn.disabled) return;
          realBtn.click();
        });
      }

      function applyReviewThreshold() {
        var rating = bar.querySelector('[data-js-sticky-atc-v1-rating]');
        if (!rating) return;

        // Nothing rendered at all — no reviews yet, so there is nothing to show.
        if (!(rating.textContent || '').trim()) {
          rating.remove();
          return;
        }

        var min = parseInt(bar.dataset.minReviews, 10);
        if (!min || min < 1) return; // 0 or blank disables the rule

        var countEl = rating.querySelector('.oke-sr-count');
        if (!countEl) return;

        var digits = (countEl.textContent || '').replace(/[^0-9]/g, '');
        if (!digits) return;

        if (parseInt(digits, 10) < min) rating.remove();
      }
      applyReviewThreshold();

      /* Label + state mirroring. The real button's text and data-* attributes change
         when the shopper picks a sold-out variant (it becomes the Klaviyo notify-me
         button), so without this the bar goes stale. */
      function syncState() {
        if (!proxyBtn) return;

        if (proxyLabel) {
          var labelSource = realBtn.querySelector('.lp-pdp__personalize-text') || realBtn;
          var text = (labelSource.textContent || '').replace(/\s+/g, ' ').trim();
          if (text && proxyLabel.textContent !== text) proxyLabel.textContent = text;
        }

        proxyBtn.disabled = !!realBtn.disabled;
        bar.classList.toggle('is-bis', realBtn.dataset.lpBisNotify === 'true');
      }

      /* Price mirroring. .lp-pdp__price is written by component-lp-pdp-gallery.js on
         variant change, via ShopliftPrice — reading it here means an active Shoplift
         price test carries over instead of being reset to the Shopify price. */
      function syncPrice() {
        if (!pagePrice) return;
        var onSale = salePrice && document.documentElement.classList.contains('sale-pricing-active');
        var text = ((onSale ? salePrice : pagePrice).textContent || '').replace(/\s+/g, ' ').trim();
        if (!text) return;
        // Both price slots read the same source: the in-button suffix and the
        // standalone line under the title. Either may be absent per the settings.
        if (proxyPrice && proxyPrice.textContent !== text) proxyPrice.textContent = text;
        if (proxyPriceLine && proxyPriceLine.textContent !== text) {
          proxyPriceLine.textContent = text;
        }
      }

      var stateObserver = new MutationObserver(syncState);
      stateObserver.observe(realBtn, {
        attributes: true,
        childList: true,
        subtree: true,
        characterData: true,
      });
      syncState();

      if (pagePrice) {
        var priceObserver = new MutationObserver(syncPrice);
        priceObserver.observe(pagePrice, {
          childList: true,
          subtree: true,
          characterData: true,
        });
        if (salePrice) {
          priceObserver.observe(salePrice, {
            childList: true,
            subtree: true,
            characterData: true,
          });
          document.addEventListener('sale-pricing:change', syncPrice);
        }
        syncPrice();
      }

      /* Lift the Redo chat bubble clear of the bar, keeping its own 20px gap.
         Measured rather than hard-coded so it tracks the bar's real height (90px
         mobile / 92px desktop) without duplicating those numbers in JS. */
      var setRedoLift = null;
      function syncRedoLift(above) {
        if (!setRedoLift) setRedoLift = resolveRedoLift();
        if (!setRedoLift) return;
        setRedoLift(above ? bar.offsetHeight + REDO_BASE_BOTTOM : 0);
      }

      function syncPageInset(above) {
        document.body.style.setProperty(
          '--lp-sticky-atc-inset',
          above ? bar.offsetHeight + 'px' : '0px'
        );
      }

      // Show the bar only once the real button has scrolled above the viewport.
      var visibilityObserver = new IntersectionObserver(
        function (entries) {
          var above = entries[0].boundingClientRect.bottom < 0;
          bar.classList.toggle('is-visible', above);
          bar.setAttribute('aria-hidden', above ? 'false' : 'true');
          /* Drop the attribute rather than setting tabindex="0" so the button
             returns to its natural position in the tab order. CSS visibility:hidden
             already takes it out of that order while the bar is down; this is the
             belt-and-braces half for anything that reads the attribute directly. */
          if (proxyBtn) {
            if (above) proxyBtn.removeAttribute('tabindex');
            else proxyBtn.setAttribute('tabindex', '-1');
          }
          syncRedoLift(above);
          syncPageInset(above);
        },
        { threshold: 0 }
      );
      visibilityObserver.observe(realBtn);

      // Bar height changes between the mobile and desktop specs on resize.
      window.addEventListener('resize', function () {
        if (!bar.classList.contains('is-visible')) return;
        syncRedoLift(true);
        syncPageInset(true);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyAtcV1);
  } else {
    initStickyAtcV1();
  }

  // Theme editor re-renders sections without a page reload.
  document.addEventListener('shopify:section:load', initStickyAtcV1);

  // The Live in Hoops tabs swap the whole product section in without a reload,
  // and this file is only executed once however many tabs are opened — so the
  // bar that arrives with each new product needs initialising here. Same hook
  // component-lp-pdp-gallery.js and component-okendo-review-summary.js use.
  document.addEventListener('lih:pdp-loaded', initStickyAtcV1);
})();
